import React, { useEffect, useRef, useState } from "react";

import Loader from "../Common/Loader";
import { createPdfDocument } from "helper/pdfjs";

interface PdfPreviewProps {
  file: string | Uint8Array;
}

interface PdfViewport {
  height: number;
  width: number;
}

interface PdfPage {
  getViewport: (scale: number) => PdfViewport;
  render: (options: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport }) => {
    promise: Promise<void>;
  };
}

interface PdfDocument {
  destroy: () => void;
  getPage: (pageNumber: number) => Promise<PdfPage>;
  numPages: number;
}

export default function PdfPreview({ file }: PdfPreviewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isRendering, setIsRendering] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let pdfDocument: PdfDocument | null = null;

    async function renderPdf() {
      setIsRendering(true);
      setHasError(false);

      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      try {
        const loadingTask = createPdfDocument<PdfDocument>(
          file,
          typeof file === "string" ? { withCredentials: true } : {}
        );

        pdfDocument = await loadingTask.promise;

        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
          if (cancelled) {
            return;
          }

          const page = await pdfDocument.getPage(pageNumber);

          const viewport = page.getViewport(1.4);

          const canvas = document.createElement("canvas");

          const context = canvas.getContext("2d");

          if (!context) {
            throw new Error("Unable to create PDF canvas context");
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          canvas.className = "DocumentLibraryDetailViewer-pdfPage";

          containerRef.current?.appendChild(canvas);

          await page.render({
            canvasContext: context,
            viewport,
          }).promise;
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setHasError(true);
        }
      } finally {
        if (!cancelled) {
          setIsRendering(false);
        }
      }
    }

    renderPdf();

    return () => {
      cancelled = true;

      if (pdfDocument) {
        pdfDocument.destroy();
      }
    };
  }, [file]);

  return (
    <>
      {isRendering && <Loader message="Chargement du document..." />}

      {hasError && <p className="ListMessage">Impossible d’afficher le document.</p>}

      <div ref={containerRef} />
    </>
  );
}
